import { Slider as MuiSlider, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import InfoTooltip from '../../InfoTooltip/Info-tooltip';
import { useDebounce } from '../../../hooks/useDebounce';

const boxShadow =
  '0 3px 1px rgba(0,0,0,0.1),0 2px 10px rgba(0,0,0,0.13),0 0 0 1px rgba(0,0,0,0.02)';

const CustomSlider = styled(MuiSlider)(({ theme }) => ({
  color: '#cccccc',
  flex: 1,
  height: 3,
  padding: '15px 0',
  '& .MuiSlider-thumb': {
    height: 12,
    width: 12,
    backgroundColor: '#5272e4',
    boxShadow: boxShadow,
    '&:focus, &:hover, &.Mui-active': {
      boxShadow:
        '0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.3),0 0 0 1px rgba(0,0,0,0.02)',
      '@media (hover: none)': {
        boxShadow: boxShadow,
      },
    },
  },
  '& .MuiSlider-valueLabel': {
    fontSize: 12,
    fontWeight: 'normal',
    top: -6,
    backgroundColor: 'unset',
    color: theme.palette.text.primary,
    '&:before': {
      display: 'none',
    },
    '& *': {
      background: 'transparent',
      color: theme.palette.mode === 'dark' ? '#ccc' : '#000',
    },
  },
  '& .MuiSlider-track': {
    border: 'none',
    backgroundColor: '#5272e4',
  },
  '& .MuiSlider-rail': {
    opacity: 0.5,
    backgroundColor: '#cccccc',
  },
  '& .MuiSlider-mark': {
    backgroundColor: '#cccccc',
    height: 10,
    width: 1,
    '&.MuiSlider-markActive': {
      opacity: 1,
      backgroundColor: 'currentColor',
    },
  },
}));

const Slider = ({
  name,
  label,
  formik,
  tooltipContent,
  onChange,
  brokePercentage,
  formTouched,
  not100Percent,
  initialWeight,
}: {
  name: string;
  label: string;
  formik: any;
  initialWeight: number;
  tooltipContent?: string;
  onChange: Function;
  brokePercentage: boolean;
  formTouched: boolean;
  not100Percent: boolean;
}) => {
  const [value, setValue] = useState<number | string | Array<number | string>>(
    initialWeight
  );

  const debouncedValue = useDebounce(value, 200);

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    onChange(name);

    setValue(newValue);
  };

  useEffect(() => {
    formik.setFieldValue(name, debouncedValue);
  }, [debouncedValue]);

  return (
    <div className={'mb-5'}>
      <p>{label}</p>

      <div className={'flex items-center gap-3'}>
        <CustomSlider
          value={typeof value === 'number' ? value : 0}
          onChange={handleSliderChange}
          color="secondary"
          aria-labelledby={label}
          min={0}
          max={100}
        />
        <span
          className={` w-14 border rounded-xl p-2 flex items-center justify-center cursor-default ${
            formTouched && not100Percent && !brokePercentage
              ? 'text-negative border-negative'
              : 'border-grey-10'
          } `}
        >
          {typeof value === 'number' &&
            (value / 100).toLocaleString(undefined, {
              style: 'percent',
              minimumFractionDigits: 0,
            })}
        </span>
      </div>
    </div>
  );
};

export function WithTooltip({ content, children, className = '' }) {
  return (
    <div className={'flex items-center gap-3 ' + className}>
      {children}
      <InfoTooltip content={content} isTooltipIconVisible />
    </div>
  );
}

export default Slider;
